import { tw } from "@careinfo/tailwind";
import React, { useRef, useState } from "react";
import APIS from "../../constant/Endpoint";
import useGetHook from "../../customHooks/useGetHook";
import useUploadFileHook from "../../customHooks/useUploadFileHook";
import {
  FileText,
  FileImage,
  FileAudio,
  FileVideo,
  FileArchive,
  FileCheck,
  FileX,
  File,
} from "lucide-react";
import usePatchHook from "../../customHooks/usePatchHook";
import Modal from "../Reusable/Modal";
import TextField from "../Reusable/TextInput";
import { useForm } from "react-hook-form";
import Button from "../Reusable/Button";
import EditIcon from "../../Icons/EditIcon";
import useDeleteHook from "../../customHooks/useDeleteHook";
import { toast } from "react-toastify";
import { postApiData } from "../../helpers/AxiosInstance";
import FileOptions from "../Reusable/FileOptions";
import CustomFileViewer from "../Reusable/FileViewer";
import Preview from "../Reusable/Preview";
import RenameFile from "./RenameFile";
import QrModal from "../Reusable/QrModal";

const FileUpload = ({ setOpenUploadFilePopup, object_id }) => {
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [openQrUploadPopup, setOpenQrUploadPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>({
    id: "",
    title: "",
  });
  const [isUploaded, setIsuploaded] = useState(true);
  const [isOpenModalPopup, setIsOpenModalPopup] = useState(false);
  const [isOpenDeletePopup, setIsOpenDeletePopup] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const MAX_FILE_SIZE_MB = 5;
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const isImageFile = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    return ["png", "jpg", "jpeg", "gif", "webp"].includes(ext || "");
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({});

  const { uploadFile, progress } = useUploadFileHook({
    setIsuploaded,
    queryKey: ["file"],
  });

  const { data: fileList, isLoading } = useGetHook({
    queryKey: ["file"],
    url: APIS.FILE_HANDLER,
    params: { object_id },
  });

  const { mutateAsync, isLoading: isPatchloading } = usePatchHook({
    queryKey: ["file"],
  });
  const { mutateAsync: deleteAsync, isLoading: isdeleting } = useDeleteHook({
    queryKey: ["file"],
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error("File size must be less than 5 MB");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
      handleUpload(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error("File size must be less than 5 MB");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
      handleUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const generateFileId = () => {
    return (
      "file_" +
      Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
    );
  };

  const handleUpload = async (file: File) => {
    const fileId = generateFileId();
    await uploadFile(file, fileId, object_id, () => {
      setOpenUploadFilePopup(false);
    });
  };

  const handleFileRename = async (values: any) => {
    const url = `${APIS.FILE_HANDLER}${selectedFile?.id}/`;
    const formData = {
      ...values,
    };
    try {
      const res = await mutateAsync({ url, formData, setError: () => {} });
      if (res?.status === 200) {
        reset();
        setIsOpenModalPopup(false);
      }
    } catch (err) {
      console.error(err);
    }
  };
  const handleDelete = async () => {
    const url = `${APIS.FILE_HANDLER}${selectedFile?.id}/`;
    const formData = {};
    try {
      const res = await deleteAsync({ url, formData, setError: () => {} });
      if (res?.success) {
        setIsOpenDeletePopup(false);
        setSelectedFile({ id: "", title: "" });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getFileIconComponent = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "pdf":
      case "doc":
      case "docx":
      case "txt":
        return <FileText className={tw`w-8 h-8 text-blue-600`} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return <FileImage className={tw`w-8 h-8 text-green-600`} />;
      case "mp3":
      case "wav":
        return <FileAudio className={tw`w-8 h-8 text-purple-600`} />;
      case "mp4":
      case "avi":
        return <FileVideo className={tw`w-8 h-8 text-pink-600`} />;
      case "zip":
      case "rar":
      case "7z":
        return <FileArchive className={tw`w-8 h-8 text-yellow-600`} />;
      case "json":
        return <FileCheck className={tw`w-8 h-8 text-teal-600`} />;
      case "exe":
        return <FileX className={tw`w-8 h-8 text-red-600`} />;
      default:
        return <File className={tw`w-8 h-8 text-gray-500`} />;
    }
  };

  const handleDownload = async (file: any) => {
    if (!file?.file) return;
    try {
      const response = await fetch(file.file);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.title || "download";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleQrGenerate = async () => {
    const url = APIS.GENERATE_QR;
    const formData = {
      id: object_id,
    };
    try {
      const res = await postApiData({
        url,
        formData,
        responseType: "blob",
      });
      if (res?.status === 200) {
        const qrBlob = res.data;
        const qrUrl = URL.createObjectURL(qrBlob);
        setQrImage(qrUrl);
        setOpenQrUploadPopup((prev) => !prev);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate QR");
    }
  };

  // console.log(Array.isArray(fileList) && fileList?.length > 0);
  console.log(selectedFile?.id, "what is selected");
  return (
    <div className={tw``}>
      <div className={tw`w-full space-y-6 `}>
        {!isUploaded ? (
          <div
            className={tw`flex flex-col items-center justify-center border border-dashed h-44 bg-[#F2F2F2]`}
          >
            <div className={tw`w-64 bg-gray-200 rounded-full h-4`}>
              <div
                className={tw`bg-green-600 h-4 rounded-full transition-all duration-300`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className={tw`text-xs text-gray-700 mt-2`}>
              Uploading: {progress}%
            </div>
          </div>
        ) : (
          <div
            className={tw`border border-dashed ${
              isDragging
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-gray-50"
            } p-8 text-center rounded cursor-pointer h-44 flex flex-col justify-center items-center bg-[#F2F2F2] transition-colors`}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <p className={tw`text-gray-600`}>
              Drag and drop files here or click to select files
            </p>
            <p className={tw`text-sm text-gray-400 mt-1`}>
              Accepted types: images/pdf, text/plain
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation(), handleQrGenerate();
              }}
              className={tw`px-3 py-2 bg-primary text-white rounded-md focus:outline-none`}
            >
              Generate QR
            </button>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className={tw`hidden`}
            />
          </div>
        )}

        <div className={tw`grid grid-cols-1 gap-4`}>
          {isLoading
            ? [...Array(5)].map((_, index) => (
                <div
                  key={index}
                  className={tw`border rounded w-full h-52  shadow-sm relative bg-[#F2F2F2] animate-pulse`}
                ></div>
              ))
            : Array.isArray(fileList) &&
              fileList?.length > 0 && (
                <div className={tw`grid grid-cols-5 gap-4`}>
                  {fileList?.map((item) => {
                    const title = item?.title || "";
                    const filePath = item?.file;
                    const extensionMatch = title.match(/\.(\w+)$/);
                    const fileType = extensionMatch
                      ? extensionMatch[1].toLowerCase()
                      : "";

                    return (
                      <div
                        key={item?.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewFile(item);
                          setSelectedFile({
                            id: item?.id,
                            url: filePath,
                            title: title,
                            type: fileType,
                          });
                          setIsPreviewOpen(true);
                        }}
                        className={tw`bg-[#DEDEDEC2] focus:outline-none cursor-pointer hover:bg-gray-300 rounded-lg  shadow-md`}
                      >
                        <div className={tw`p-2 flex justify-between`}>
                          <p className={tw`text-sm truncate `}>{title}</p>
                          <FileOptions
                            setSelected={setSelectedFile}
                            file={item}
                            setIsPreviewOpen={setIsPreviewOpen}
                            setOpenRenameFolderPopup={setIsOpenModalPopup}
                            setIsOpenDeletePopup={setIsOpenDeletePopup}
                            isdeleteLoading={isdeleting}
                          />
                        </div>
                        {[
                          "pdf",
                          "docx",
                          "xlsx",
                          "png",
                          "jpg",
                          "mp4",
                          "jpeg",
                        ].includes(fileType) ? (
                          <CustomFileViewer
                            filePath={filePath}
                            fileType={fileType}
                          />
                        ) : (
                          <div
                            className={tw`h-32 flex items-center justify-center text-gray-500`}
                          >
                            <span>
                              <File /> {fileType.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
        </div>

        {isPreviewOpen && selectedFile?.id && (
          <Preview
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
          />
        )}
      </div>

      <Modal
        isOpen={isOpenModalPopup}
        title={"Rename"}
        setIsOpen={setIsOpenModalPopup}
      >
        <RenameFile
          selected={selectedFile}
          setOpenRenameFolderPopup={setIsOpenModalPopup}
        />
      </Modal>
      <Modal
        isOpen={isOpenDeletePopup}
        title={"Are You Sure?"}
        setIsOpen={setIsOpenDeletePopup}
      >
        <div
          className={tw`flex h-32 w-60 gap-x-6 my-2 justify-center items-center`}
        >
          <button
            className={tw`mt-2 text-white font-semibold bg-gray-400 px-4 py-2 rounded-md focus:outline-none`}
            onClick={() => setIsOpenDeletePopup(false)}
          >
            Cancel
          </button>
          <button
            className={tw`mt-2 text-white font-semibold bg-primary px-4 py-2 rounded-md focus:outline-none`}
            disabled={isdeleting || false}
            onClick={() => handleDelete()}
          >
            Delete
          </button>
        </div>
      </Modal>
      <div onClick={(e) => e.stopPropagation()}>
        <QrModal
          title={"Scan QR to upload"}
          isQrOpen={openQrUploadPopup}
          setIsQrOpen={setOpenQrUploadPopup}
        >
          <div className={tw`mt-4 w-96`}>
            <img
              src={qrImage}
              alt="Generated QR Code"
              className={tw`w-full h-64 object-contain border p-2 rounded`}
            />
          </div>
        </QrModal>
      </div>
      {/* <Modal
        title="File Preview"
        isOpen={isPreviewOpen}
        setIsOpen={setIsPreviewOpen}
      >
        <div className={tw`w-[500px]`}>
          {previewFile && isImageFile(previewFile?.title) ? (
            <img
              src={previewFile.file}
              alt={previewFile.title}
              className={tw`w-full h-96 object-contain rounded`}
            />
          ) : (
            <div className={tw`flex flex-col items-center gap-4`}>
              {previewFile ? getFileIconComponent(previewFile?.title) : null}
              <p>{previewFile?.title}</p>
            </div>
          )}

          <div className={tw`flex justify-center mt-4`}>
            <button
              className="text-white font-semibold bg-primary px-4 py-2 rounded-md focus:outline-none"
              onClick={() => handleDownload(previewFile)}
            >
              Download
            </button>
          </div>
        </div>
      </Modal> */}
    </div>
  );
};

export default FileUpload;
